"use client";

import { pushAppHistoryUrl } from "@/lib/next-history-marker";
import { PublicOrgHostGuard } from "@/lib/public-org-host-gate";
import type { PublicHelpSpace } from "@v1/backend/logic/pages/public_help_space_groups";
import { useTranslations } from "next-intl";
import { usePathname } from "next/navigation";
import {
  type ReactElement,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useState,
} from "react";
import { HelpArticleClient } from "./help-article-client";
import { HelpSpaceList } from "./help-children-list";
import { type HelpSite, HelpSiteProvider } from "./help-link-base";
import { HelpNavTree } from "./help-nav-tree";
import { HelpNotFoundInline } from "./help-not-found-inline";
import { HelpSearchTrigger } from "./help-header";
import { HelpShell } from "./help-shell";
import {
  type HelpSpaTarget,
  helpSpaHref,
  helpSpaHrefFromBase,
  isPlainLeftClick,
  parseDocsSpaPath,
  parseHelpSpaPath,
} from "./help-spa-path";
import { HelpSpaceOverview } from "./help-space-overview";

/** `ssr` = the server-rendered route is still on screen. */
type SpaMode = { readonly kind: "ssr" } | HelpSpaTarget;

/** Keeps the shell mounted; in-app help links become SPA navigation, not Next soft-nav (metadata refetch). */
export function HelpOrgWorkspace({
  org_name,
  org_slug,
  logo_url,
  spaces,
  site,
  children,
}: {
  readonly org_name: string;
  readonly org_slug: string;
  readonly logo_url: string | null;
  readonly spaces: readonly PublicHelpSpace[];
  readonly site: HelpSite;
  readonly children: ReactNode;
}): ReactElement {
  const t = useTranslations("public.helpOrgWorkspace");
  const pathname = usePathname();
  const onDocs = site.docs !== null;
  const parsePath = useCallback(
    (path: string): HelpSpaTarget | null =>
      onDocs ? parseDocsSpaPath(path) : parseHelpSpaPath(path, org_slug),
    [onDocs, org_slug],
  );
  const hrefOf = useCallback(
    (target: HelpSpaTarget): string =>
      onDocs ? helpSpaHrefFromBase("", target) : helpSpaHref(org_slug, target),
    [onDocs, org_slug],
  );
  const [mode, setMode] = useState<SpaMode>({ kind: "ssr" });
  // From the request path, so the server-rendered sidebar already opens the right branch.
  const [current, setCurrent] = useState<HelpSpaTarget | null>(() =>
    parsePath(pathname),
  );

  const show = useCallback(
    (target: HelpSpaTarget): void => {
      setCurrent(target);
      setMode(target);
      if (target.kind === "center") {
        document.title = t("hilfeWissenOrg", { org: org_name });
      } else if (target.kind === "space") {
        const space = spaces.find((item) => item.space_id === target.spaceId);
        if (space) document.title = `${space.name} – ${org_name}`;
      }
    },
    [org_name, spaces, t],
  );

  useEffect(() => {
    function onPopState(): void {
      const target = parsePath(window.location.pathname);
      if (target) show(target);
    }
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [parsePath, show]);

  const navigateSpa = useCallback(
    (target: HelpSpaTarget): void => {
      pushAppHistoryUrl(hrefOf(target));
      show(target);
      // A new page starts at its top, like a full load.
      window.scrollTo({ top: 0 });
    },
    [hrefOf, show],
  );

  const onCaptureClick = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>): void => {
      if (!isPlainLeftClick(event)) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      const anchor = target.closest("a");
      if (!(anchor instanceof HTMLAnchorElement)) return;
      if (anchor.target === "_blank" || anchor.hasAttribute("download")) return;
      const hrefAttr = anchor.getAttribute("href");
      if (!hrefAttr || hrefAttr.startsWith("#")) return;
      let url: URL;
      try {
        url = new URL(hrefAttr, window.location.origin);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin) return;
      const parsed = parsePath(url.pathname);
      if (!parsed) return;
      event.preventDefault();
      navigateSpa(parsed);
    },
    [navigateSpa, parsePath],
  );

  let main: ReactNode;
  if (mode.kind === "ssr") {
    main = children;
  } else if (mode.kind === "center") {
    main = (
      <div className="mx-auto w-full max-w-[720px]">
        <h1 className="text-[26px] font-bold leading-tight tracking-tight text-primary">
          {t("koennenHelfen")}
        </h1>
        <HelpSearchTrigger size="hero" />
        {spaces.length === 0 ? (
          <div className="mt-10 rounded-lg border border-border/60 bg-muted/[0.18] px-5 py-8 text-center text-[13px] text-primary/50">
            {t("hierGibtKeineOeffentlichen")}
          </div>
        ) : (
          <div className="mt-7">
            <HelpSpaceList spaces={spaces} org_slug={org_slug} />
          </div>
        )}
      </div>
    );
  } else if (mode.kind === "space") {
    const space = spaces.find((item) => item.space_id === mode.spaceId);
    main = space ? (
      <HelpSpaceOverview
        org_slug={org_slug}
        space={space}
        includeJsonLd={false}
      />
    ) : (
      <HelpNotFoundInline />
    );
  } else {
    // A section header page opens as its section, like the server route.
    const section = spaces.find((item) => item.space_id === mode.pageId);
    main = section ? (
      <HelpSpaceOverview
        org_slug={org_slug}
        space={section}
        includeJsonLd={false}
      />
    ) : (
      <HelpArticleClient org_slug={org_slug} page_id={mode.pageId} />
    );
  }

  return (
    <HelpSiteProvider site={site}>
      <div onClickCapture={onCaptureClick}>
        <PublicOrgHostGuard orgSlug={org_slug} />
        <HelpShell
          org_name={org_name}
          org_slug={org_slug}
          logo_url={logo_url}
          sidebar={
            <HelpNavTree spaces={spaces} org_slug={org_slug} current={current} />
          }
        >
          {main}
        </HelpShell>
      </div>
    </HelpSiteProvider>
  );
}
